import type { components } from "@/api-schema";
import { CustomerType, VehicleBuyInStatus } from "@/api-schema-enums";
import { VEHICLE_COLOR_LABELS } from "@/common/helpers/vehicle-type-labels";
import { isAxiosError } from "axios";

type ElectronicSignatureDocumentLink =
  | Pick<components["schemas"]["ElectronicSignature"], "signedDocumentUrl">
  | null
  | undefined;

export interface VehicleBuyInCustomerFieldVisibility {
  showBirthDate: boolean;
  showBusinessFields: boolean;
}

const SIGNED_DOCUMENT_STATUSES = new Set([
  VehicleBuyInStatus.ContractCompleted,
  VehicleBuyInStatus.V1SyncFailed,
  VehicleBuyInStatus.Completed,
]);

const MANUAL_V1_SYNC_STATUSES = new Set([
  VehicleBuyInStatus.ContractCompleted,
  VehicleBuyInStatus.V1SyncFailed,
  VehicleBuyInStatus.Completed,
]);

export function isVehicleBuyInCancellationAvailable(status: VehicleBuyInStatus): boolean {
  return status === VehicleBuyInStatus.ContractSent || status === VehicleBuyInStatus.ContractCancellationFailed;
}

export function getVehicleBuyInColorLabel(color: string | null | undefined, language: string): string | undefined {
  const normalizedColor = color?.trim();
  if (!normalizedColor) {
    return undefined;
  }

  const normalizedLanguage = language.trim().toLowerCase();
  const labelLanguage = normalizedLanguage === "de" || normalizedLanguage.startsWith("de-") ? "de" : "en";

  return VEHICLE_COLOR_LABELS[normalizedColor]?.[labelLanguage] ?? normalizedColor;
}

export function getOpenableSignedDocumentUrl(
  status: VehicleBuyInStatus,
  electronicSignature: ElectronicSignatureDocumentLink,
): string | undefined {
  if (!SIGNED_DOCUMENT_STATUSES.has(status)) {
    return undefined;
  }

  return electronicSignature?.signedDocumentUrl?.trim() || undefined;
}

export function canSyncVehicleBuyInToV1(
  status: VehicleBuyInStatus,
  hasPermission: boolean,
  isFeatureEnabled: boolean,
): boolean {
  return hasPermission && isFeatureEnabled && MANUAL_V1_SYNC_STATUSES.has(status);
}

export function resolveVehicleBuyInV1SyncErrorMessage(error: unknown, fallbackMessage: string): string {
  if (!isAxiosError<unknown>(error)) {
    return fallbackMessage;
  }

  const data = error.response?.data;
  if (!data || typeof data !== "object" || !("message" in data)) {
    return fallbackMessage;
  }

  const message = data.message;
  if (typeof message === "string") {
    return message.trim() || fallbackMessage;
  }

  if (Array.isArray(message) && message.every((entry): entry is string => typeof entry === "string")) {
    const messages = message.map((entry) => entry.trim()).filter(Boolean);
    return messages.join("; ") || fallbackMessage;
  }

  return fallbackMessage;
}

export function getVehicleBuyInCustomerFieldVisibility(
  customerType: CustomerType,
): VehicleBuyInCustomerFieldVisibility {
  return {
    showBirthDate: customerType === CustomerType.Private,
    showBusinessFields: customerType === CustomerType.Business,
  };
}
